import React, { useEffect, useState } from "react";
import { TextField, Switch, Box, Grid, Backdrop, Button } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { makeStyles, Theme } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import { useFormik } from "formik";
import { object, string, number } from "yup";
import {
  getAllFileCompressionConfigs,
  addUpdateFileCompressionConfig,
} from "src/app-redux/configuration/actions/configurationActions";
import {
  loader,
  selectAllFileCompressionConfigsList,
} from "src/app-redux/configuration/configurationSlice";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import { IAddFileCompression } from "src/common/typings/configuration";
import NewCommonButton from "src/components/CommonButton/NewCommonButton";
import CommonButton from "../../../../../../components/CommonButton/CommonButton";
import { columns } from "./columns";
import { Prompt } from "react-router-dom";
import MultiSelect2 from "src/components/molecules/MultiSelect2";
import { fileCompressionFileTypes } from "src/common/utils/app.dropdowns";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import { Check, Close } from "@material-ui/icons";
import { Tooltip } from "@mui/material";
import CommonLoader from "src/components/Loader/CommonLoader";

const useStyles = makeStyles((theme: Theme) => ({
  formRow: {
    marginBottom: "0px",
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
  tabCardContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "auto",
  },

  UserMainPanel: {
    width: "100%",
    float: "left",
    padding: "0px 20px",
    paddingRight: 0,
  },
  mainConainer: { marginTop: "0px" },
  multiselectConfigFileCompression: {
    position: "relative",
    bottom: "35px",
    "& .searchWrapper": {
      border: "none",
      maxHeight: "35px",
      overflow: "auto",
    },
  },
}));

export interface IForm extends IAddFileCompression {}
interface Props {}

export function InputFields({}: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const [isEditMode, setisEditMode] = React.useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  const loading = useAppSelector(loader);
  const allFileCompressionConfigRows = useAppSelector(selectAllFileCompressionConfigsList);
  const [selectedFileTypes, setSelectedFileTypes] = useState<string[]>([]);

  const allFileTypes = fileCompressionFileTypes;

  // Form Submission
  const validationSchema = object({
    fileSizeThreshold: number()
      .required("This field is required")
      .max(100, "Maximum 100MB is allowed"),
    fileTypes: string().required("This field is required"),
  });
  const formik = useFormik<IAddFileCompression>({
    enableReinitialize: true,
    initialValues: {
      fileSizeThreshold: 0,
      fileTypes: "",
      compressionEnabled: false,
      isActive: true,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, actions) => {
      const res = await dispatch(addUpdateFileCompressionConfig(values));
      if (res?.payload) {
        dispatch(getAllFileCompressionConfigs());
        actions.setSubmitting(false);
        actions.resetForm();
        setisEditMode(false);
        setIsDirty(false);
        setSelectedRecord(null);
        setSelectedFileTypes([]);
      }
    },
  });
  // End Form Submission
  const handleUpdateClick = (params: any) => {
    setisEditMode(true);
    setSelectedRecord(params);
    const fileTypes = params.fileTypes.split(",");
    const arr: any[] = [];
    fileTypes.forEach((item: any) => {
      const type = allFileTypes.find((file) => file.typeName === item);
      if (type) arr.push(type);
    });
    setSelectedFileTypes(arr);
    formik.setFieldValue("fileSizeThreshold", params.fileSizeThreshold);
    formik.setFieldValue("fileTypes", params.fileTypes.split(",").toString());
    formik.setFieldValue("compressionEnabled", params.compressionEnabled);
    formik.setFieldValue("isActive", params.isActive);
  };
  useEffect(() => {
    dispatch(getAllFileCompressionConfigs());
  }, []);

  /* Transform Column */
  useEffect(() => {
    if (allFileCompressionConfigRows.length) {
      const transformedColumns: any = columns.map((column: any) =>
        column.accessorKey === "fileSizeThreshold"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return <>{` ${row.original.fileSizeThreshold} MB `}</>;
              },
            }
          : column.accessorKey === "actions"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <>
                    <NewCommonButton
                      onClick={() => {
                        handleUpdateClick(row.original);
                        setIsDirty(true);
                      }}
                      icon={<EditIcon />}
                      isEdit={true}
                    >
                      Edit
                    </NewCommonButton>
                  </>
                );
              },
            }
          : column.accessorKey === "compressionEnabled"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.compressionEnabled ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.compressionEnabled ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column
      );

      setDisplayColumns(transformedColumns);
    }
  }, [allFileCompressionConfigRows]);

  const onMultiSelectSelection = (selectedList: any) => {
    const fileTypes: string = selectedList
      .map((item: any) => (item.typeName ? item.typeName : ""))
      .toString();
    formik.setFieldValue("fileTypes", fileTypes);
  };
  const onMultiSelectRemove = (selectedList: any) => {
    const fileTypes: string = selectedList
      .map((item: any) => (item.typeName ? item.typeName : ""))
      .toString();
    formik.setFieldValue("fileTypes", fileTypes);
  };

  // Show prompt if the form is fully or partially filled and user wants to navigate away
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  useEffect(() => {
    // If prompt, when reloading or closing the tab
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);
  function handleChange(e: any) {
    // Textfield ==>> e.target.value
    // Checkbox / Switch ==>> e.target.checked !== e.target._wrapperState.initialChecked

    formik.handleChange(e);
    e.target.value ? setIsDirty(true) : setIsDirty(false);
  }

  return (
    <>
      {/* If prompt, when navigating to another page inside the app */}
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <form onSubmit={formik.handleSubmit}>
        <Backdrop className={classes.backdrop} open={loading}>
          <CommonLoader />
        </Backdrop>
        <Grid container direction="row" alignItems="flex-start">
          {/* First Section */}
          <>
            <Grid className={classes.UserMainPanel}>
              <Grid
                // Container
                container
                className={classes.mainConainer}
                direction="row"
                alignItems="flex-start"
                spacing={2}
              >
                <Grid container item xs={12} spacing={2}>
                  {isEditMode ? (
                    <Grid container item md={12} lg={12} spacing={2} style={{ marginBottom: 0 }}>
                      {/* File Size Threshold */}
                      <Grid
                        item
                        xs={12}
                        md={4}
                        lg={4}
                        style={{ paddingLeft: "0px", paddingRight: 0 }}
                      >
                        <Box>
                          <TextField
                            className="customTextField"
                            type="number"
                            required
                            size="small"
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            label="File Size Threshold"
                            placeholder="Enter File ize Threshold"
                            name="fileSizeThreshold"
                            disabled={false}
                            value={formik.values.fileSizeThreshold}
                            onChange={handleChange}
                            error={
                              formik.touched.fileSizeThreshold &&
                              Boolean(formik.errors.fileSizeThreshold)
                            }
                            helperText={
                              formik.touched.fileSizeThreshold && formik.errors.fileSizeThreshold
                            }
                          />
                        </Box>
                      </Grid>
                      {/* File Types */}
                      <Grid
                        item
                        xs={12}
                        md={5}
                        lg={4}
                        style={{ paddingLeft: "0px", paddingRight: 0, paddingBottom: 0 }}
                      >
                        <Box sx={{ marginLeft: { xs: 0, md: "8px" } }}>
                          <TextField
                            className="customTextField"
                            required
                            size="small"
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            onKeyDown={(event: any) => {
                              if (event.key !== "Tab") event.preventDefault();
                            }}
                            label="File Types"
                            disabled={false}
                            error={formik.touched.fileTypes && Boolean(formik.errors.fileTypes)}
                            helperText={formik.touched.fileTypes && formik.errors.fileTypes}
                          />
                          <Box className={classes.multiselectConfigFileCompression}>
                            <MultiSelect2
                              allValues={allFileTypes}
                              selctedItems={selectedFileTypes}
                              displayValue={"typeName"}
                              placeholder=""
                              onSelect={onMultiSelectSelection}
                              onRemove={onMultiSelectRemove}
                            />
                          </Box>
                        </Box>
                      </Grid>
                      {/* Compression Enabled? */}
                      <Grid item xs={9} md={3} style={{ padding: 0 }}>
                        <Box
                          sx={{ marginLeft: { xs: 0, md: "15px" } }}
                          style={{ marginTop: "8px" }}
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(formik.values.compressionEnabled)}
                                onChange={formik.handleChange}
                                name="compressionEnabled"
                                color="primary"
                              />
                            }
                            label="Compression Enabled"
                          />
                        </Box>
                      </Grid>
                      {/* Save Reset Buttons */}
                      <Grid item xs={3} md={12} lg={1} style={{ paddingRight: 0 }}>
                        <Box mb={1} mt={1} style={{ float: "right" }}>
                          <CommonButton
                            isGreen
                            cssClass="customSubmitBtn"
                            disable={
                              selectedRecord
                                ? formik.values.fileSizeThreshold ==
                                    selectedRecord.fileSizeThreshold &&
                                  formik.values.fileTypes == selectedRecord.fileTypes &&
                                  formik.values.compressionEnabled ==
                                    selectedRecord.compressionEnabled
                                : !formik.isValid ||
                                  JSON.stringify(formik.values) ===
                                    JSON.stringify(formik.initialValues)
                            }
                            onClick={() => {
                              formik.submitForm();
                            }}
                          >
                            {isEditMode ? "Update" : "Add"}
                          </CommonButton>
                        </Box>
                      </Grid>
                    </Grid>
                  ) : (
                    <div></div>
                  )}
                  {/* Grid */}
                  <Grid md={12} style={{ marginTop: isEditMode ? "-25px" : 0 }}>
                    <CommonMaterialGrid
                      defaultPerPageRecord={10}
                      height={400}
                      rows={allFileCompressionConfigRows || []}
                      columns={displayColumns}
                    />
                  </Grid>
                </Grid>
                {/* End Inner Container */}
              </Grid>
              {/* End Outer Container */}
            </Grid>
          </>
        </Grid>
      </form>
    </>
  );
}
